import { action, computed, makeObservable, observable } from "mobx";

import { deleteCookie } from "../utils/StringUtilities";
import { getCdnUrl } from "../utils/SchoolBlocksUtilities";

type UnreadNotifications = {
  total: number;
  messages: boolean;
};

export type IUserStoreInitData = {
  user: SoprisAppsProps["pageProps"]["user"];
  currentOrganization: SoprisAppsProps["pageProps"]["currentOrganization"];
  organization?: SoprisAppsProps["pageProps"]["organization"];
  featureFlags?: { posthog_tracking_enabled?: { enabled: boolean } };
  unreadNotifications?: UnreadNotifications;
};

export default class UserStore {
  id = null;
  username = null;
  firstName = null;
  lastName = null;
  _avatar: string | null = null;
  admin = false;
  viewer = false;
  editor = false;
  follower = false;
  unreadNotifications = {
    total: 0,
    messages: false,
  };
  initData: IUserStoreInitData | null = null;

  supportChatOpen = false;
  initialized = false;
  posthogTrackingEnabled = false;
  organizationId: number | string | null = null;

  constructor(initData: IUserStoreInitData | undefined) {
    this.initialize(initData);

    makeObservable(this, {
      id: observable,
      username: observable,
      firstName: observable,
      lastName: observable,
      _avatar: observable,
      admin: observable,
      viewer: observable,
      editor: observable,
      follower: observable,
      updateUser: action,
      setPermissions: action,
      logout: action,
      isAdmin: computed,
      isEditor: computed,
      unreadNotifications: observable,
      setUnreadNotifications: action,
      supportChatEnabled: computed,
      supportChatOpen: observable,
      openSupportChat: action,
      closeSupportChat: action,
    });
  }

  initialize = (initData: IUserStoreInitData | undefined) => {
    if (initData) {
      this.initData = {
        user: initData.user,
        currentOrganization: initData.currentOrganization,
        featureFlags: initData.featureFlags,
      };

      this.posthogTrackingEnabled =
        initData.featureFlags?.posthog_tracking_enabled?.enabled ?? false;
      this.organizationId = initData.currentOrganization?.id ?? null;

      if (initData.user) {
        this.updateUser(initData.user);
      }

      if (initData.unreadNotifications) {
        this.setUnreadNotifications(initData.unreadNotifications);
      }

      this.setPermissions(initData.currentOrganization.user_permissions);
      this.initialized = true;
    }
  };

  serialize: () => IUserStoreInitData = () => {
    return {
      user: (this.initData as IUserStoreInitData).user,
      currentOrganization: (this.initData as IUserStoreInitData)
        .currentOrganization,
      featureFlags: {
        posthog_tracking_enabled: { enabled: this.posthogTrackingEnabled },
      },
      unreadNotifications: this.unreadNotifications,
    };
  };

  get isAdmin() {
    return this.admin || false;
  }

  get isEditor() {
    return this.editor || false;
  }

  get avatar() {
    return getCdnUrl(this._avatar || "");
  }

  openSupportChat() {
    this.supportChatOpen = true;
  }

  closeSupportChat() {
    this.supportChatOpen = false;
  }

  get supportChatEnabled() {
    return this.isAdmin || this.isEditor;
  }

  updateUser(user) {
    user = user || {};

    if (user.hasOwnProperty("id")) {
      this.id = user.id;
    }

    if (user.hasOwnProperty("username")) {
      this.username = user.username;
    }

    if (user.hasOwnProperty("fname")) {
      this.firstName = user.fname;
    }

    if (user.hasOwnProperty("lname")) {
      this.lastName = user.lname;
    }

    if (user.hasOwnProperty("avatar")) {
      this._avatar = user.avatar;
    }
  }

  setPermissions = (permissions) => {
    // loop through because permissions object may not contain all permission levels
    for (let k in permissions) {
      if (permissions.hasOwnProperty(k)) {
        this[k] = permissions[k];
      }
    }
  };

  setUnreadNotifications = (notificationsObj: {
    total: number;
    messages: boolean;
  }) => {
    this.unreadNotifications = notificationsObj;
  };

  logout = async () => {
    this.id = null;
    this.username = null;
    this.firstName = null;
    this.lastName = null;
    this._avatar = null;
    this.admin = false;
    this.editor = false;
    this.follower = false;
    this.supportChatOpen = false;
    await deleteCookie("token");
    await deleteCookie("context");

    if (typeof MC !== "undefined") {
      MC.reactReauth(null);
    }

    // Reset PostHog to clear identified user
    if (this.posthogTrackingEnabled && typeof window !== "undefined") {
      import("posthog-js").then(({ default: posthog }) => {
        posthog.reset();
      });
    }
  };
}
